/**
 * CountryRiskCharts — Phase 36 Batch Z (GMIA Phase 3).
 *
 * 4 small sparkline charts showing the historical drivers of the
 * Currency Risk Score we already render in the badge tooltip:
 *
 *   • Volatility (annualised σ of monthly returns)
 *   • Cumulative devaluation vs base (negative = currency lost value)
 *   • Inflation (annual %, World Bank FP.CPI.TOTL.ZG)
 *   • Political stability (WGI PV.PER.RNK percentile)
 *
 * Data: `GET /api/fx/risk/history?currency=&user_base=&years=`
 *
 * Rendered as an expandable panel below the CurrencyRiskBadge.
 * Hidden entirely for same-currency deals (caller controls).
 */
import { useEffect, useState } from "react";
import {
  AreaChart, Area, LineChart, Line, ResponsiveContainer,
  Tooltip as RcTooltip, XAxis, YAxis,
} from "recharts";
import { Loader2, AlertTriangle, Activity, TrendingDown, Coins, Vote } from "lucide-react";
import api from "@/lib/api";

const FACTOR_META = [
  {
    key: "fx_volatility_series",
    label: "FX volatility",
    suffix: "%",
    desc: "Annualised σ of monthly returns",
    Icon: Activity,
    color: "#f59e0b",
    accent: "amber",
    chart: "area",
    digits: 1,
  },
  {
    key: "devaluation_series",
    label: "Cumulative devaluation",
    suffix: "%",
    desc: "Move vs base since t-N",
    Icon: TrendingDown,
    color: "#ef4444",
    accent: "rose",
    chart: "line",
    digits: 1,
  },
  {
    key: "inflation_series",
    label: "Inflation",
    suffix: "%",
    desc: "Annual change, CPI",
    Icon: Coins,
    color: "#0ea5e9",
    accent: "sky",
    chart: "line",
    digits: 1,
  },
  {
    key: "political_stability_series",
    label: "Political stability",
    suffix: "",
    desc: "WGI percentile (0 worst, 100 best)",
    Icon: Vote,
    color: "#10b981",
    accent: "emerald",
    chart: "area",
    digits: 0,
  },
];

function SparkTooltip({ active, payload, suffix, digits }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  const x = p.payload?.date || p.payload?.year || "";
  const v = p.value;
  return (
    <div className="rounded-md border border-slate-200 bg-white/95 px-2 py-1 text-[10px] font-medium text-slate-700 shadow-sm">
      <div className="text-slate-500">{x}</div>
      <div className="tabular-nums">
        {v != null ? `${Number(v).toFixed(digits)}${suffix}` : "—"}
      </div>
    </div>
  );
}

function FactorChart({ meta, series }) {
  const { Icon, label, suffix, desc, color, chart, digits, key } = meta;
  const xKey = key === "inflation_series" || key === "political_stability_series" ? "year" : "date";
  const data = (series || []).map((p) => ({ ...p, value: Number(p.value) }));
  const latest = data.length ? data[data.length - 1].value : null;
  const earliest = data.length ? data[0].value : null;
  const delta = latest != null && earliest != null ? latest - earliest : null;

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
      data-testid={`risk-factor-${key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <Icon className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" />
            <p className="truncate text-xs font-semibold text-slate-700">{label}</p>
          </div>
          <p className="mt-0.5 text-[10px] text-slate-400">{desc}</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-semibold tabular-nums text-slate-900">
            {latest != null ? `${latest.toFixed(digits)}${suffix}` : "—"}
          </p>
          {delta != null && Math.abs(delta) > 0.05 ? (
            <p
              className={`text-[10px] font-medium tabular-nums ${
                delta > 0 ? "text-emerald-600" : "text-rose-600"
              }`}
            >
              {delta > 0 ? "+" : ""}
              {delta.toFixed(digits)}
              {suffix} since t-N
            </p>
          ) : null}
        </div>
      </div>
      <div className="mt-2 h-16 w-full">
        {data.length === 0 ? (
          <div className="flex h-full items-center justify-center text-[10px] text-slate-400">
            No data
          </div>
        ) : chart === "area" ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <XAxis dataKey={xKey} hide />
              <YAxis hide domain={["dataMin", "dataMax"]} />
              <RcTooltip
                content={(props) => (
                  <SparkTooltip {...props} suffix={suffix} digits={digits} />
                )}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={1.5}
                fill={`url(#grad-${key})`}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <XAxis dataKey={xKey} hide />
              <YAxis hide domain={["dataMin", "dataMax"]} />
              <RcTooltip
                content={(props) => (
                  <SparkTooltip {...props} suffix={suffix} digits={digits} />
                )}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={1.5}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export default function CountryRiskCharts({ currency, userBase = "USD", years = 5 }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currency) return undefined;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api
      .get("/fx/risk/history", { params: { currency, user_base: userBase, years } })
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((e) => {
        if (!cancelled) setErr(e?.message || "Failed to load risk history");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currency, userBase, years]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-slate-400" data-testid="country-risk-charts-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="ml-2 text-xs">Loading risk history…</span>
      </div>
    );
  }
  if (err) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
        {err}
      </div>
    );
  }
  if (!data || data.data_coverage === "same-currency") return null;

  const isSnapshot = data.data_coverage === "snapshot";

  return (
    <div data-testid="country-risk-charts" className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {currency} risk drivers · last {years}y
        </p>
        {isSnapshot ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800">
            <AlertTriangle className="h-3 w-3" />
            Snapshot data
          </span>
        ) : null}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {FACTOR_META.map((meta) => (
          <FactorChart key={meta.key} meta={meta} series={data[meta.key]} />
        ))}
      </div>
    </div>
  );
}
